/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// core
import { TextField } from '@material-ui/core';

import clsx from 'clsx';

import Select from '../../Select';
import Button from '../../Button';

import { EDIT_MODE, VIEW_MODE } from '../../../utils/const/taskModes';

import useStyles from '../styles';
import QuestionShell from '../QuestionShell';
import useQuestionForm, { QUESTION_FIELDS } from '../QuestionShell/useQuestionForm';

function DropDownMenu({
  identifier,
  pages,
  info,
  handleDeleteQuestion,
  handleDuplicateQuestion,
  mode,
  setPages,
}) {
  const styles = useStyles();
  const { t } = useTranslation();

  const [showInputs, setShowInputs] = useState(false);
  const initialId = 1;
  const [correct, setCorrect] = useState(['']);
  const [options, setOptions] = useState(
    info ? info.QuestionChoices : pages[identifier - 1].questionChoices || [],
  );
  const [selectedValue, setSelectedValue] = useState('');

  const question = useQuestionForm({
    identifier,
    pages,
    info,
    setPages,
    writeVia: 'setPages',
    autoLoadMedia: false,
  });
  const {
    loadMedia, scheduleWrite, questionLogic, syncLocalField,
  } = question;

  useEffect(() => {
    loadMedia(info || pages?.[identifier - 1]);
  }, [identifier, info, pages, loadMedia]);

  const handleOnChange = useCallback((event) => {
    const { id, value, imageUrl } = event.target;

    syncLocalField(id, value);

    if (QUESTION_FIELDS.includes(id)) {
      scheduleWrite({
        [id]: value,
        questionLogic,
        questionAnswer: correct,
        imageUrl,
      });
      return;
    }

    const indexOption = options.findIndex(
      (option) => option.identifier === id,
    );
    if (indexOption === -1) return;

    const newOptions = [...options];
    newOptions[indexOption] = { ...newOptions[indexOption], title: value };
    setOptions(newOptions);

    scheduleWrite({
      questionChoices: newOptions,
      questionAnswer: correct,
    });
  }, [correct, options, questionLogic, scheduleWrite, syncLocalField]);

  const handleAddOption = () => {
    setShowInputs(true);
    if (options.length && !options[options.length - 1].title) return;

    const generatedId = initialId + options.length;
    setOptions([
      ...options,
      {
        id: generatedId,
        identifier: `${generatedId}`,
        title: '',
        type: 'text',
      },
    ]);
    setShowInputs(false);
  };

  const parseOptions = () => options.map((option, index) => ({
    name: option.title || ' ',
    value: option.id,
    position: index + 1,
  }));

  const handleCorrectAnswer = (identifierid) => {
    setCorrect([identifierid]);
    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionAnswer: [identifierid],
    };
  };

  return (
    <QuestionShell
      {...question}
      handleOnChange={handleOnChange}
      identifier={identifier}
      info={info}
      mode={mode}
      titleKey="task.taskEdition.dropDownMenuText.question"
      titleMaxLength={70}
      descriptionAs="none"
      handleDeleteQuestion={handleDeleteQuestion}
      handleDuplicateQuestion={handleDuplicateQuestion}
    >
      <Select
        name="selected"
        value={selectedValue}
        items={parseOptions()}
        className={`${styles.description} ${styles.textfield}`}
        onChange={(e) => {
          const { value } = e.target;
          setSelectedValue(value);
          const index = options.findIndex((opt) => opt.id === Number(value));
          handleCorrectAnswer(index + 1);
        }}
        fullwidth
      />
      {showInputs ? (
        <div key={options[options.length - 1].identifier}>
          <TextField
            id={options[options.length - 1].identifier}
            className={styles.option}
            value={options[options.length - 1].title}
            placeholder={`${t(
              'task.taskEdition.multipleSelectionText.write',
            )} ${t('task.taskEdition.multipleSelectionText.option')}`}
            InputProps={{ disableUnderline: true }}
            onChange={mode === EDIT_MODE ? handleOnChange : null}
            disabled={mode === VIEW_MODE}
          />
        </div>
      ) : null}
      {mode !== VIEW_MODE ? (
        <Button
          onClick={handleAddOption}
          className={clsx({
            [styles.hide]: info,
          })}
        >
          {showInputs
            ? t('task.taskEdition.dropDownMenuText.sendNewOption')
            : t('task.taskEdition.dropDownMenuText.addNewOption')}
        </Button>
      ) : null}
    </QuestionShell>
  );
}

export default DropDownMenu;
